import React, { useEffect } from 'react';

/** Approached and never reached, so the resistance grows the whole way instead of hitting a wall. */
const FURTHEST = 190;
/** How easily it gives at the very start. Lower is stiffer. */
const GIVE = 0.5;
/** A rate, not a spring: a speed carries past where it was going and wobbles against a live swipe. */
const HOMEWARD = 0.72;
/** And never less than this, or the last few pixels crawl in after everything else has arrived. */
const LEAST = 14;

/** The curve a phone uses to say a list has ended. */
const stretched = (travelled: number): number => (travelled * FURTHEST * GIVE) / (FURTHEST + GIVE * travelled);

/** Held in pixels so the pull works on what is seen; a push goes back through the curve to be added. */
const travelFor = (at: number): number => (at * FURTHEST) / (GIVE * (FURTHEST - at));

/** Without it the leftover swipe takes the browser back a page; the box moves, since a transform would unstick the name column. */
export const useEdgeBounce = (box: React.RefObject<HTMLElement | null>): void => {
  useEffect(() => {
    const grid = box.current;
    if (!grid) return undefined;

    const gentle = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    /** How far the grid is being held from home, in pixels on screen. */
    let held = 0;
    let frame: number | null = null;

    const draw = (): void => {
      grid.style.transform = held === 0 ? '' : `translateX(${held}px)`;
    };

    // Lifted onto its own layer only while moving: a layer never taken back costs memory all session.
    const lift = (on: boolean): void => {
      grid.style.willChange = on ? 'transform' : '';
    };

    const home = (): void => {
      held -= Math.max(held * HOMEWARD, LEAST);
      // Home is where it stops: overshooting would take the grid off its own start.
      if (held <= 0) {
        held = 0;
        frame = null;
        draw();
        lift(false);
        return;
      }
      draw();
      frame = requestAnimationFrame(home);
    };

    const onWheel = (event: WheelEvent): void => {
      // Sideways gestures only. A scroll down the page that drifts is still a scroll down the page.
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      // Only once there is no beginning left: until then the swipe is scrolling the grid.
      if (event.deltaX >= 0 || grid.scrollLeft > 0) return;

      // Held off the browser where it can be, which is what keeps the leftover swipe from navigating.
      event.preventDefault();
      if (gentle?.matches) return;

      // Push and pull compete frame by frame: a throw's tail cannot outrun the pull, a real push can.
      held = stretched(travelFor(held) + -event.deltaX);
      draw();
      if (frame === null) {
        lift(true);
        frame = requestAnimationFrame(home);
      }
    };

    // Not React's `onWheel`: a passive listener cannot hold the gesture off the browser.
    grid.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      grid.removeEventListener('wheel', onWheel);
      if (frame !== null) cancelAnimationFrame(frame);
      grid.style.transform = '';
      lift(false);
    };
  }, [box]);
};
